import { format, parseISO } from "date-fns";
import { Calendar, MapPin } from "lucide-react";

import { cn } from "../utils/cn";

/**
 * When and where, under an event's name.
 *
 * `layout` rather than a caller-supplied `flex-col`, which is how the card
 * asked for a stack before. A COLUMN flex container that also has
 * `flex-wrap: wrap` sizes each line to its widest item instead of stretching
 * items to the container, so the venue line kept its full 520px and hung out
 * of the card on a phone. The two arrangements are different enough to name.
 */
export function EventMeta({
  startDate,
  endDate,
  location,
  className,
  layout = "inline",
}: {
  startDate?: string;
  endDate?: string;
  location?: string | null;
  className?: string;
  /** "inline" wraps date and place onto one line where they fit; "stack" puts
   *  each on its own line, for a narrow column such as a card. */
  layout?: "inline" | "stack";
}) {
  if (!startDate && !endDate && !location) return null;

  return (
    <div
      className={cn(
        "flex min-w-0 text-sm text-muted-foreground",
        layout === "stack" ? "flex-col gap-1.5" : "flex-wrap gap-x-4 gap-y-2",
        className,
      )}
    >
      {(startDate || endDate) && (
        <span className="flex min-w-0 max-w-full items-center gap-1.5">
          <Calendar className="h-4 w-4 shrink-0" aria-hidden="true" />
          {startDate && formatEventDate(startDate)}
          {endDate && endDate !== startDate && (
            <> &ndash; {formatEventDate(endDate)}</>
          )}
        </span>
      )}
      {location && (
        <span className="flex min-w-0 max-w-full items-center gap-1.5">
          <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
          {/* `min-w-0` on the truncating span itself, not only on its parent:
              `truncate` sets white-space: nowrap, which otherwise makes this
              flex item's automatic minimum width the whole venue address. */}
          <span className="min-w-0 truncate">{location}</span>
        </span>
      )}
    </div>
  );
}

export function formatEventDate(value: string): string {
  try {
    return format(parseISO(value), "d MMMM yyyy");
  } catch {
    return value;
  }
}
